import { __awaiter } from "tslib";
import { useEffect, useState } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { useGetReleaseNotes, useLazyDownloadReleaseNoteAsText } from '@/platform-services/release-notes';
import { logError } from '@/utils/logger';
import { useReleaseNotesItemsSelector } from '@/store/selectors';
import { cacheReleaseNotes } from '@/store/actions/creators';
/**
 * Fetches the release-notes descriptor list, downloads any missing Markdown content in
 * parallel, and writes the result into the Redux cache via `cacheReleaseNotes`.
 *
 * `isLoading` is true while the descriptor list is being fetched OR while any content
 * files are being downloaded.
 */
export const useReleaseNotesFetch = () => {
    const storedItems = useReleaseNotesItemsSelector();
    const dispatch = useAppDispatch();
    const [isDownloading, setIsDownloading] = useState(false);
    const { data: dataDescriptors, isLoading: isListLoading, isError: isListError, error: listError, } = useGetReleaseNotes();
    const { triggerDownloadAsText } = useLazyDownloadReleaseNoteAsText();
    useEffect(() => {
        if (isListError) {
            logError('Error fetching release notes:', listError);
        }
    }, [isListError, listError]);
    useEffect(() => {
        if (!(dataDescriptors === null || dataDescriptors === void 0 ? void 0 : dataDescriptors.length))
            return;
        const syncCache = () => __awaiter(void 0, void 0, void 0, function* () {
            const itemsToFetch = dataDescriptors
                .filter((data) => { var _a; return !((_a = storedItems.byId[data.fileName]) === null || _a === void 0 ? void 0 : _a.content); })
                .map((data) => ({ data, promise: triggerDownloadAsText(data.fileName) }));
            if (!itemsToFetch.length)
                return;
            setIsDownloading(true);
            try {
                const values = yield Promise.all(itemsToFetch.map((item) => item.promise));
                const fetched = itemsToFetch.map((item, index) => (Object.assign(Object.assign({}, item), { data: Object.assign(Object.assign({}, item.data), { content: values[index] }) })));
                dispatch(cacheReleaseNotes(fetched.map((i) => i.data)));
            }
            catch (error) {
                logError('Error fetching release notes:', error);
            }
            finally {
                setIsDownloading(false);
            }
        });
        syncCache().catch((e) => {
            var _a;
            logError((_a = e === null || e === void 0 ? void 0 : e.message) !== null && _a !== void 0 ? _a : 'useReleaseNotesFetch -> error');
        });
        // storedItems is included intentionally: when cacheReleaseNotes dispatches and updates the
        // store, this effect re-runs. The filter `!storedItems.byId[fileName]?.content` ensures
        // already-cached files are skipped immediately, so there is no infinite re-fetch loop.
    }, [dataDescriptors, dispatch, storedItems, triggerDownloadAsText]);
    return { isLoading: isListLoading || isDownloading };
};
